import { Fragment, useState, useRef, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Checkbox from '@mui/material/Checkbox';
import ButtonGroup from '@mui/material/ButtonGroup';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import CheckIcon from '@mui/icons-material/Check';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';

interface SplitButtonProps {
    label?: string;
    options: string[];
    search?: boolean;
    single?: boolean;
    multipleLabel?: string;
    selectHandler?: (n: string[]) => void;
    selected?: string[];
}

const SearchTextField = styled(TextField)({
    borderRadius: 5,
    backgroundColor: 'text.primary',
    width: '90%',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: '10px',
    marginBottom: '5px',
    '& label.Mui-focused': {
        color: 'text.primary',
    },
    '& .MuiInput-underline:after': {
        borderBottomColor: 'text.primary',
    },
    '& .MuiOutlinedInput-root': {
        fontSize: '11px',
        lineHeight: '11px',

        '& .MuiInputBase-input': {

            padding: '5px 15px',
        },

        '& fieldset': {
            borderColor: 'text.primary',
        },
        '&:hover fieldset': {
            borderColor: 'text.primary',
        },
        '&.Mui-focused fieldset': {
            borderColor: 'text.primary',
        },
    },
});

export default function MultipleDropDownMenu({ single, label, options, search, multipleLabel, selectHandler, selected }: SplitButtonProps) {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<HTMLDivElement>(null);

    const defaultIndex: Set<number> = new Set();
    const [selectedIndex, setSelectedIndex] = useState(defaultIndex);
    const [selectedOption, setSelectedOption] = useState('');

    useEffect( () => {
        if (selected) {
            const preselectedIdx: Set<number> = new Set();
            let idx = 0;
            for (const opt of options) {
                if (selected.includes(opt)){
                    preselectedIdx.add(idx);
                }
                idx++;
            }
            setSelectedIndex(preselectedIdx);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (selectedIndex.size === 0) {
            if (label !== undefined) {
                setSelectedOption(label);
            } else {
                setSelectedOption('Select');
            }
        } else if (selectedIndex.size === 1) {
            const itr = selectedIndex.values();
            const selectedValue = itr.next().value;
            setSelectedOption(options[selectedValue]);
        } else {
            let multipleLabelName = 'Multiple Selected';
            if (multipleLabel !== undefined) {
                multipleLabelName = `(${selectedIndex.size}) ${multipleLabel} Selected`;
            }
            setSelectedOption(multipleLabelName);
        }
        const selectedIndexArray = Array.from(selectedIndex);
        if (selectHandler) {
            const selectedItems: string[] = [];
            for (const sel of selectedIndexArray) {
                selectedItems.push(options[sel]);
            }
            selectHandler(selectedItems);
        }
    
    }, [selectedIndex]); // eslint-disable-line react-hooks/exhaustive-deps

    // const handleClick = () => {
    //     console.info(`You clicked`);
    // };

    const handleMenuItemClick = (
        event: React.ChangeEvent<HTMLInputElement>,
        index: number,
    ) => {

        //if all unchecked
        if (!single) {
            if (!event.currentTarget.checked && selectedIndex.has(index) && selectedIndex.size === 1) {
                console.log("unchecked && selected index only 1");
                setSelectedIndex(new Set());

            } else if (event.currentTarget.checked && selectedIndex.size === 0) {
                setSelectedIndex(new Set([index]));

            } else if (event.currentTarget.checked && selectedIndex.size >= 0) {
                const newIndex = new Set(selectedIndex.add(index).values());
                setSelectedIndex(newIndex);

            } else if (!event.currentTarget.checked) {
                const newIndex: Set<number> = new Set();
                selectedIndex.forEach((val, idx) => {
                    console.log(`${val}:${idx}`);
                    if (idx !== index) {
                        newIndex.add(idx);
                    }
                });
                setSelectedIndex(newIndex);
            }
        } else {
            if (event.currentTarget.checked) {
                setSelectedIndex(new Set([index]));
            } else {
                setSelectedIndex(new Set());
            }

        }
      
    };


    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event: Event) => {
        if (
            anchorRef.current
        ) {
            return;
        }

        setOpen(false);
    };



    return (
        <Fragment>
            <ButtonGroup color="info" disableElevation={true} variant="text" ref={anchorRef}
                sx={{
                    [`.MuiButtonGroup-grouped:not(:last-of-type)`]: {
                        borderColor: 'action.active',
                        minWidth: '140px'
                    },
                }}
            >
                <Button
                    sx={{
                        fontSize: '16px',
                        justifyContent: 'start',
                        backgroundColor: 'action.active',
                        pl: '10px',
                        color: selectedIndex.size > 0 ? 'primary.main' : 'text.primary'
                    }}
                    >{selectedOption}</Button>
                <Button
                    size="small"
                    onClick={handleToggle}
                    sx={{
                        backgroundColor: 'action.active'
                    }}
                >
                    <KeyboardArrowDownIcon fontSize="medium" sx={{ color: 'secondary.main' }} />
                </Button>
            </ButtonGroup>
            <Popper
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition={true}
                disablePortal={true}
                placement='bottom-start'
                style={{
                    zIndex: 200
                }}
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin:
                                placement === 'bottom' ? 'left top' : 'left bottom',
                        }}
                    >
                        <Paper sx={{ width: '100%', minWidth: '190px', zIndex: 9 }}>
                            <ClickAwayListener onClickAway={handleClose}>
                                <Stack id="split-button-menu" spacing={0}>

                                    {search && <SearchTextField size="small"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon color="secondary"
                                                        fontSize='small'
                                                    />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />}

                                    {options.map((option, index) => (
                                        <Stack key={index} direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Checkbox
                                                checked={selectedIndex.has(index) ? true : false}
                                                sx={{
                                                    p: 1,
                                                    color: 'lightgray',
                                                    '.MuiSvgIcon-root': {
                                                        border: '1px solid lightgray',
                                                        borderRadius: '2px'
                                                    }
                                                }}
                                                icon={<CheckIcon fontSize="small" sx={{ color: 'white' }} />}
                                                checkedIcon={<CheckIcon fontSize="small" sx={{ color: 'secondary.main' }} />}
                                                onChange={(event) => handleMenuItemClick(event, index)}
                                            />
                                            <Typography sx={{ fontSize: 11, flex: '1 0 20%' }}>{option}</Typography>
                                            <Typography sx={{ fontSize: 10, color: 'grey.600', flex: '1 0 10%', textAlign: 'right', pr: 1 }}>(2000)</Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </Fragment>
    );
}